import React, { createContext, useState } from 'react';
import { Platform } from 'react-native';
import constants from '@/config/constants';
import { parse, useURL } from 'expo-linking';
import {
  exchangeCodeAsync,
  ResponseType,
  useAuthRequest,
} from 'expo-auth-session';
import { maybeCompleteAuthSession } from 'expo-web-browser';
import axios from 'axios';

export const EpaAuthContext = createContext(null);
maybeCompleteAuthSession({ skipRedirectCheck: true });

const EpaAuthProvider = ({ children }) => {
  const url = useURL();

  const discoveryDocument = React.useMemo(
    () => ({
      authorizationEndpoint: constants.idp.authorizationEndpoint,
      tokenEndpoint: constants.idp.tokenEndpoint,
    }),
    [],
  );

  const clientId =
    Platform.OS === 'web'
      ? constants.idp.clientIdWeb
      : constants.idp.clientIdApp;
  const redirectUri =
    Platform.OS === 'web'
      ? constants.idp.redirectUrlWeb
      : constants.idp.redirectUrlApp;

  let [authTokenCallback, setAuthTokenCallback] = useState();
  const startAuth = async (callback) => {
    setAuthTokenCallback(() => callback);
    await promptAsync();
  };

  const exchangeAccessForAuthToken = async (accessToken) => {
    try {
      return await exchangeCodeAsync(
        {
          clientId,
          code: accessToken,
          redirectUri,
          extraParams: {
            code_verifier: request.codeVerifier,
          },
        },
        discoveryDocument,
      );
    } catch {
      throw new Error('Error exchanging access token for auth token');
    }
  };

  const [request, response, promptAsync] = useAuthRequest(
    {
      clientId,
      responseType: ResponseType.Code,
      redirectUri,
      scopes: constants.idp.scopes,
      usePKCE: true,
    },
    discoveryDocument,
  );

  React.useEffect(() => {
    if (Platform.OS !== 'web' && url) {
      const params = parse(url).queryParams;
      const redirectUrl = `${redirectUri}?code=${params.code}&state=${params.state}`;
      executeCodeExchangeMimotoDeepLink(
        redirectUrl,
        params.state,
        params.code,
      ).then((url) => {
        const paramsFromMimoto = parse(url).queryParams;
        exchangeAccessForAuthToken(paramsFromMimoto.code).then((authTokens) =>
          authTokenCallback(authTokens.idToken),
        );
      });
    }
  }, [url]);

  React.useEffect(() => {
    if (Platform.OS === 'web' && response && response.type === 'success') {
      exchangeAccessForAuthToken(response.params.code).then((authTokens) =>
        authTokenCallback(authTokens.idToken),
      );
    }
  }, [response]);

  const executeCodeExchangeMimotoDeepLink = async (deepLink, state, code) => {
    try {
      const response = await axios.post(
        constants.idp.mimotoCodeExchangeEndpoint,
        {
          redirectUrl: deepLink,
          code,
          clientId,
          state,
        },
      );

      return response.data.redirectUrl;
    } catch (error) {
      throw error;
    }
  };

  return (
    <EpaAuthContext.Provider
      value={{
        startAuth,
      }}
    >
      {children}
    </EpaAuthContext.Provider>
  );
};

export default EpaAuthProvider;
